import { useMutation, useQueryClient } from '@tanstack/react-query';
import { meKeys } from '@/features/settings/api/meKeys';
import { authClient } from '@/lib/api/authClient';
import { apiClient } from '@/lib/api/client';

import { type AccessTokenResult, accessTokenResultSchema } from '../schemas/authSchemas';
import { useAuthStore } from '../store/authStore';
import { useOnboardingStore } from '../store/onboardingStore';

export type CompleteOnboardingInput = {
  firstName: string;
  lastName: string;
  workspaceName: string;
  industry?: string;
  teamSize?: string;
};

// Two-step onboarding completion:
//
// Step 1 — POST auth/complete-onboarding (workspaceName, industry?, teamSize?)
//   signcare-auth creates the Tenant + TenantAdmin role + Membership in Auth DB,
//   marks onboarding done, returns a JWT with tenant_id + given_name + family_name.
//   Names come from the Auth user entity (saved earlier by PATCH /auth/profile).
//
// Step 2 — POST /api/v1/tenants (JSON: { name, industry?, size? })
//   Studio provisions its tenant shadow + TenantAdmin role + Membership + API creds.
//   The handler reads tenant_id from the JWT (set by setAccessTokenSession in step 1).
//   The user shadow gets firstName/lastName from currentUser.FirstName/LastName (JWT claims).
//   This step is NOT in a try/catch — failure must surface so the user can retry.
export function useCompleteOnboarding() {
  const markOnboardingComplete = useAuthStore((s) => s.markOnboardingComplete);
  const setAccessTokenSession = useAuthStore((s) => s.setAccessTokenSession);
  const clearOnboardingDraft = useOnboardingStore((s) => s.clear);
  const queryClient = useQueryClient();

  return useMutation<AccessTokenResult, Error, CompleteOnboardingInput>({
    mutationFn: async (input) => {
      // Step 1: signcare-auth owns onboarding.
      const raw = await authClient
        .post('auth/complete-onboarding', {
          json: {
            firstName: input.firstName,
            lastName: input.lastName,
            workspaceName: input.workspaceName,
            industry: input.industry,
            teamSize: input.teamSize,
          },
        })
        .json();
      const result = accessTokenResultSchema.parse(raw);

      // Update auth + tenant store so the next apiClient call carries
      // the new JWT (with tenant_id) in both Authorization and X-Tenant-Id.
      setAccessTokenSession(result);

      // Step 2: provision Studio's tenant shadow, roles, membership, and API credentials.
      // Sends JSON so there are no multipart boundary or Content-Type issues.
      await apiClient.post('api/v1/tenants', {
        json: {
          name: input.workspaceName,
          industry: input.industry || undefined,
          size: input.teamSize || undefined,
        },
      });

      return result;
    },
    onSuccess: (result) => {
      setAccessTokenSession(result);
      markOnboardingComplete();
      clearOnboardingDraft();
      queryClient.invalidateQueries({ queryKey: meKeys.detail() });
    },
  });
}
